import React, { useState, useEffect } from 'react';
import { useResumeContext } from './context/ResumeContext';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import IntroductionModule from './components/modules/IntroductionModule';
import FrameworkModule from './components/modules/FrameworkModule';
import CareerJourneyModule from './components/modules/CareerJourneyModule';
import CompetencyModule from './components/modules/CompetencyModule';
import PortfolioModule from './components/modules/PortfolioModule';
import ConclusionModule from './components/modules/ConclusionModule';
import { LoadingSpinner } from './components/shared/animations';

/**
 * Main App Component
 * 
 * Provides the overall structure and navigation for the interactive resume.
 * Implements smooth transitions between sections and loading states.
 */
const App = () => {
  const { activeSection } = useResumeContext();
  const [isLoading, setIsLoading] = useState(false);
  const [previousSection, setPreviousSection] = useState('');

  // Track section changes for transition direction
  useEffect(() => {
    // Skip the initial render
    if (previousSection === activeSection) {
      return;
    }

    if (!previousSection) {
      setPreviousSection(activeSection);
      return;
    }

    // Simulate loading state on section change
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
      setPreviousSection(activeSection);
    }, 300); // Short loading time for smooth transition

    return () => clearTimeout(timer);
  }, [activeSection, previousSection]);

  // Determine transition direction based on section order
  const getTransitionDirection = () => {
    const sections = ['intro', 'framework', 'career', 'competency', 'portfolio', 'conclusion'];
    const currentIndex = sections.indexOf(activeSection);
    const previousIndex = sections.indexOf(previousSection);
    
    if (currentIndex > previousIndex) {
      return { x: 50, opacity: 0 }; // Moving forward (right to left)
    } else {
      return { x: -50, opacity: 0 }; // Moving backward (left to right)
    }
  };

  // Map of section IDs to their component
  const sectionComponents = {
    intro: <IntroductionModule />,
    framework: <FrameworkModule />,
    career: <CareerJourneyModule />,
    competency: <CompetencyModule />,
    portfolio: <PortfolioModule />,
    conclusion: <ConclusionModule />
  };

  return (
    <div className="flex flex-col bg-slate-50 text-slate-800 min-h-screen">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto w-full p-4 md:p-6">
        {/* Loading spinner */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" color="blue" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={getTransitionDirection}
              animate={{ x: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              {/* Render the active section component */}
              {sectionComponents[activeSection] || sectionComponents.intro}
            </motion.div>
          </AnimatePresence>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default App;
